// WizardScaffold.jsx
// Shared scaffolding for the multi-step creation wizards (Create a Workspace,
// Submit a Job): a numbered stepper, per-step headers, and a tab panel.
import { Fragment } from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";

import {
  ACCENT,
  ACCENT_TINT,
  INK,
  INK_SECONDARY,
  INK_MUTED,
  HAIRLINE,
  SURFACE_MUTED,
  FONT,
  STATUS,
  WEIGHT,
  withAlpha,
} from "../../../../theme/tokens";

// Only renders its children when active; keeps the tabpanel semantics.
export function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`wizard-tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}
TabPanel.propTypes = {
  children: PropTypes.node,
  value: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
};

// Horizontal numbered stepper: completed steps fill with the accent + check,
// the active step gets an accent ring, upcoming steps are quietly muted.
export function WizardStepper({
  steps,
  active,
  isStepEnabled,
  hasStepError,
  onStep,
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        width: "100%",
        // Query container: step text shows/hides based on THIS bar's width
        // (not the viewport), since the surrounding pane width varies with the
        // sidebar and the config-preview panel.
        containerType: "inline-size",
      }}
    >
      {steps.map((step, i) => {
        const completed = i < active;
        const isActive = i === active;
        const enabled = isStepEnabled(i);
        const hasError = hasStepError(i);
        return (
          <Fragment key={step.label}>
            <Box
              role="button"
              tabIndex={enabled ? 0 : -1}
              onClick={() => enabled && onStep(i)}
              onKeyDown={(e) => {
                if (enabled && (e.key === "Enter" || e.key === " ")) {
                  e.preventDefault();
                  onStep(i);
                }
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                cursor: enabled ? "pointer" : "default",
                opacity: enabled || isActive ? 1 : 0.6,
                minWidth: 0,
                outline: "none",
              }}
            >
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  fontSize: FONT.sm,
                  fontWeight: WEIGHT.bold,
                  bgcolor: hasError
                    ? withAlpha(STATUS.error, 0.1)
                    : completed
                      ? ACCENT
                      : isActive
                        ? ACCENT_TINT
                        : SURFACE_MUTED,
                  color: hasError
                    ? STATUS.error
                    : completed
                      ? "#fff"
                      : isActive
                        ? ACCENT
                        : INK_MUTED,
                  border: hasError
                    ? `1.5px solid ${STATUS.error}`
                    : isActive
                      ? `1.5px solid ${ACCENT}`
                      : `1px solid ${HAIRLINE}`,
                  transition: "all 0.2s ease",
                }}
              >
                {hasError ? (
                  "!"
                ) : completed ? (
                  <CheckIcon sx={{ fontSize: 18 }} />
                ) : (
                  i + 1
                )}
              </Box>
              {/* Labels only when the bar is wide enough for all steps;
                  otherwise the compact circles + connectors carry the progress
                  and the StepHeader below names the current step. */}
              <Box
                sx={{
                  minWidth: 0,
                  display: "none",
                  "@container (min-width: 720px)": { display: "block" },
                }}
              >
                <Typography
                  sx={{
                    fontSize: FONT.sm,
                    fontWeight: isActive ? WEIGHT.semibold : WEIGHT.medium,
                    color: hasError
                      ? STATUS.error
                      : isActive || completed
                        ? INK
                        : INK_MUTED,
                    lineHeight: 1.25,
                    whiteSpace: "nowrap",
                  }}
                >
                  {step.label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: FONT.xs,
                    color: hasError ? STATUS.error : INK_MUTED,
                    lineHeight: 1.25,
                    whiteSpace: "nowrap",
                  }}
                >
                  {step.caption}
                </Typography>
              </Box>
            </Box>
            {i < steps.length - 1 && (
              <Box
                sx={{
                  flex: 1,
                  height: 2,
                  mx: { xs: 1, sm: 1.5 },
                  borderRadius: 1,
                  minWidth: 12,
                  bgcolor: i < active ? ACCENT : HAIRLINE,
                  transition: "background-color 0.2s ease",
                }}
              />
            )}
          </Fragment>
        );
      })}
    </Box>
  );
}
WizardStepper.propTypes = {
  steps: PropTypes.array.isRequired,
  active: PropTypes.number.isRequired,
  isStepEnabled: PropTypes.func.isRequired,
  hasStepError: PropTypes.func,
  onStep: PropTypes.func.isRequired,
};
WizardStepper.defaultProps = {
  hasStepError: () => false,
};

// One clean heading per step (title + optional description).
export const StepHeader = ({ title, description }) => (
  <Box sx={{ mb: 3 }}>
    <Typography
      sx={{ fontSize: FONT.xl, fontWeight: WEIGHT.semibold, color: INK }}
    >
      {title}
    </Typography>
    {description && (
      <Typography
        sx={{
          fontSize: FONT.sm,
          color: INK_SECONDARY,
          mt: 0.75,
        }}
      >
        {description}
      </Typography>
    )}
  </Box>
);
StepHeader.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
};

export const SubHeader = ({ children, sx }) => (
  <Typography
    sx={{
      fontSize: FONT.md,
      fontWeight: WEIGHT.semibold,
      color: INK,
      mb: 1.5,
      ...sx,
    }}
  >
    {children}
  </Typography>
);
SubHeader.propTypes = {
  children: PropTypes.node,
  sx: PropTypes.object,
};
